import { useEffect, useRef, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import type { UseMutationResult } from '@tanstack/react-query';
import type { TaskWithStats, ProjectWithTasks, MoveTasksResult } from '../../services/api/tasks';

interface MoveTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  selectedTasks: TaskWithStats[];
  targetProjects: ProjectWithTasks[];
  moveMutation: UseMutationResult<
    MoveTasksResult,
    Error,
    { taskIds: number[]; targetProjectId: number }
  >;
}

export function MoveTasksModal({
  isOpen,
  onClose,
  onComplete,
  selectedTasks,
  targetProjects,
  moveMutation,
}: MoveTasksModalProps) {
  const [targetProjectId, setTargetProjectId] = useState<string>('');
  const [result, setResult] = useState<MoveTasksResult | null>(null);
  // F25-002: track the auto-close timer so unmount cancels it — otherwise
  // a parent that unmounts the modal in the 1500ms window triggers state
  // updates on an unmounted component.
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current !== null) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, []);

  const handleClose = () => {
    setTargetProjectId('');
    setResult(null);
    onClose();
  };

  const handleMove = async () => {
    if (!targetProjectId) return;

    try {
      const moveResult = await moveMutation.mutateAsync({
        taskIds: selectedTasks.map((t) => t.id),
        targetProjectId: parseInt(targetProjectId, 10),
      });
      setResult(moveResult);

      // If all tasks moved successfully, close after a brief delay
      if (moveResult.failed.length === 0) {
        if (autoCloseTimerRef.current !== null) {
          clearTimeout(autoCloseTimerRef.current);
        }
        autoCloseTimerRef.current = setTimeout(() => {
          autoCloseTimerRef.current = null;
          handleClose();
          onComplete();
        }, 1500);
      }
    } catch {
      // Error is handled by mutation
    }
  };

  const handleDone = () => {
    handleClose();
    onComplete();
  };

  const selectedProject = targetProjects.find(
    (p) => p.id === parseInt(targetProjectId, 10)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Move Tasks"
      size="md"
      footer={
        result ? (
          <div className="flex justify-end">
            <Button onClick={handleDone}>Done</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={moveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMove}
              disabled={!targetProjectId || moveMutation.isPending}
              isLoading={moveMutation.isPending}
            >
              Move {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''}
            </Button>
          </div>
        )
      }
    >
      {result ? (
        <div className="space-y-4">
          {/* Success message */}
          {result.moved.length > 0 && (
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <svg
                className="h-5 w-5 text-green-500 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Successfully moved {result.moved.length} task
                  {result.moved.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}

          {/* Failed tasks */}
          {result.failed.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800 mb-2">
                Failed to move {result.failed.length} task
                {result.failed.length !== 1 ? 's' : ''}:
              </p>
              <ul className="text-sm text-red-700 space-y-1">
                {result.failed.map((failure) => {
                  const task = selectedTasks.find((t) => t.id === failure.id);
                  return (
                    <li key={failure.id}>
                      <span className="font-medium">{task?.name || `Task #${failure.id}`}:</span>{' '}
                      {failure.reason}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-gray-600">
            Select a target project to move{' '}
            <span className="font-medium">{selectedTasks.length}</span> task
            {selectedTasks.length !== 1 ? 's' : ''} to:
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Project
            </label>
            <select
              value={targetProjectId}
              onChange={(e) => setTargetProjectId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">Select a project...</option>
              {targetProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.fullName || project.name}
                </option>
              ))}
            </select>
          </div>

          {selectedProject && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Moving to:{' '}
                <span className="font-medium">
                  {selectedProject.fullName || selectedProject.name}
                </span>
              </p>
            </div>
          )}

          {/* Task list preview */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Tasks to move:</p>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
              <ul className="divide-y divide-gray-200">
                {selectedTasks.map((task) => (
                  <li key={task.id} className="px-3 py-2 text-sm">
                    <span className="font-medium">{task.name}</span>
                    {task.code && (
                      <span className="ml-2 text-gray-500 font-mono text-xs">
                        [{task.code}]
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {moveMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                {(moveMutation.error as Error)?.message || 'Failed to move tasks'}
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
